import { RichTextEditor } from '@mantine/tiptap'
import { useEditor } from '@tiptap/react'
import Highlight from '@tiptap/extension-highlight'
import LinkExtension from '@tiptap/extension-link'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { Markdown } from 'tiptap-markdown'
import { tiptapTableExtensions } from '../tiptap_table_extensions'
import React, { forwardRef, useEffect, useImperativeHandle } from 'react'

export type WebUserDocumentMarkdownEditorHandle = {
  getMarkdown: () => string
}

function getMarkdownFromEditor(editor: ReturnType<typeof useEditor>): string {
  if (!editor) return ''
  const md = (editor.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.()
  return typeof md === 'string' ? md : ''
}

export const WebUserDocumentMarkdownEditor = forwardRef<
  WebUserDocumentMarkdownEditorHandle,
  {
    initialMarkdown: string
    readOnly?: boolean
  }
>(function WebUserDocumentMarkdownEditor(props, ref) {
  const { initialMarkdown, readOnly } = props

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Underline,
        Highlight,
        LinkExtension.configure({ openOnClick: false }),
        ...tiptapTableExtensions,
        Markdown.configure({ html: true }),
      ],
      content: initialMarkdown,
      editable: !readOnly,
    },
    [initialMarkdown],
  )

  useEffect(() => {
    editor?.setEditable(!readOnly)
  }, [editor, readOnly])

  useImperativeHandle(
    ref,
    () => ({
      getMarkdown: () => getMarkdownFromEditor(editor).trim(),
    }),
    [editor],
  )

  if (!editor) return <></>

  return (
    <RichTextEditor
      editor={editor}
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      styles={{
        content: {
          '& .ProseMirror table': {
            borderCollapse: 'collapse',
            width: '100%',
            margin: '0.75em 0',
            fontSize: 'var(--mantine-font-size-sm)',
          },
          '& .ProseMirror th, & .ProseMirror td': {
            border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            padding: '8px 12px',
            verticalAlign: 'top',
          },
          '& .ProseMirror th': {
            fontWeight: 600,
            backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
          },
        },
      }}
    >
      <RichTextEditor.Toolbar sticky stickyOffset={0}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
          <RichTextEditor.ClearFormatting />
          <RichTextEditor.Highlight />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
          <RichTextEditor.H4 />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
          <RichTextEditor.Blockquote />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>
      <RichTextEditor.Content style={{ flex: 1, minHeight: 0, overflow: 'auto' }} />
    </RichTextEditor>
  )
})

WebUserDocumentMarkdownEditor.displayName = 'WebUserDocumentMarkdownEditor'
